import { useEffect, useReducer, useState, useMemo } from "react"
import PropTypes from "prop-types"
import { withPrefix } from "gatsby"
import {
  Box,
  Button,
  Text,
  Stack,
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
  Select,
  SelectItem,
} from "@fastly/consistently"
import { MikanSpan } from "@components/Mikan"
import useMedia, { xlMediaQuery } from "@hooks/use-media"
import { Output, CurrencyOutput, EstimatorGrid, Input, TotalHeader } from "./styles"
import { RemoveButton, AddButton } from "@components/Button/Button"
import { Toggletip } from "@components/Toggletip/Toggletip"

export const RATES = {
  1: { first10TBRate: 0.12, next10TBRate: 0.08, requestsRate: 0.0075 },
  2: { first10TBRate: 0.19, next10TBRate: 0.14, requestsRate: 0.009 },
  3: { first10TBRate: 0.28, next10TBRate: 0.24, requestsRate: 0.016 },
}

export const UNITS = {
  GB: "GB",
  TB: "TB",
}

export const regionsConfig = {
  north_america: RATES[1],
  europe: RATES[1],
  asia: RATES[2],
  south_america: RATES[2],
  anz: RATES[2],
  south_korea: RATES[3],
  india: RATES[3],
  africa: RATES[3],
}

const CURRENCY_FORMAT = { style: "currency", currency: "USD" }

const ACTIONS = {
  ADD_REGION: "ADD_REGION",
  REMOVE_REGION: "REMOVE_REGION",
  ACCORDION_TOGGLE: "ACCORDION_TOGGLE",
  UPDATE_BANDWIDTH: "UPDATE_BANDWIDTH",
  UPDATE_REQUESTS: "UPDATE_REQUESTS",
}

const initialRegionState = {
  bandwidth: 0,
  units: UNITS.GB,
  requests: 0,
  subtotal: 0,
}

const initialState = {
  regions: [],
  total: { ...initialRegionState },
  accordionValues: [],
}

const allRegions = Object.keys(regionsConfig)

const ServiceForm = ({ onSubmit, regions, meta }) => {
  const initialFormState = useMemo(
    () => ({
      region: regions[0],
      units: UNITS.GB,
    }),
    [regions]
  )

  const [formState, setFormState] = useState(initialFormState)

  const handleChange = (type) => (value) => {
    setFormState((prevState) => ({ ...prevState, [type]: value }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    onSubmit(formState)
  }

  useEffect(() => {
    if (!regions.includes(formState.region)) {
      setFormState((prevState) => ({ ...prevState, region: regions[0] }))
    }
  }, [regions, formState.region])

  const formIsDisabled = regions.length <= 0

  return (
    <Box as="form" onSubmit={handleSubmit} pb="2xs">
      <Stack gap={{ _: "xs", md: "xl" }} direction="horizontal" alignItems={{ _: "center", md: "flex-end" }}>
        <Stack gap="xs" direction="horizontal" flexGrow={{ _: 1, md: 0 }}>
          <Box flexGrow={{ _: 1, md: 0 }} minWidth={{ md: "19rem" }}>
            <Select
              name="region"
              onValueChange={handleChange("region")}
              value={formState.region}
              label={meta.microcopy.region}
              disabled={formIsDisabled}
            >
              {regions.map((region) => (
                <SelectItem key={region} value={region}>
                  {meta[region] || region}
                </SelectItem>
              ))}
            </Select>
          </Box>

          <Box minWidth={"10rem"}>
            <Select
              name="units"
              onValueChange={handleChange("units")}
              value={formState.units}
              label={meta.microcopy.units}
              disabled={formIsDisabled}
            >
              <SelectItem value={UNITS.GB}>{UNITS.GB}</SelectItem>
              <SelectItem value={UNITS.TB}>{UNITS.TB}</SelectItem>
            </Select>
          </Box>
        </Stack>
        <div>
          <AddButton type="submit" disabled={formIsDisabled} aria-label={meta.microcopy.add}>
            <MikanSpan text={meta.microcopy.add} />
          </AddButton>
        </div>
      </Stack>
    </Box>
  )
}

const PricingEstimator = ({ meta = {} }) => {
  const [state, dispatch] = useReducer(reducer, initialState)
  const viewportIsLarge = useMedia(xlMediaQuery, true)

  const handleSubmit = ({ region, units }) => {
    dispatch({ type: ACTIONS.ADD_REGION, region, units })
  }

  const regionsToBeAdded = allRegions.filter((region) => !state.regions.map(({ id }) => id).includes(region))

  const handleAccordionStateUpdate = (newState) => {
    dispatch({ type: ACTIONS.ACCORDION_TOGGLE, accordionValues: newState })
  }

  return (
    <Stack mb="lg">
      <ServiceForm onSubmit={handleSubmit} regions={regionsToBeAdded} meta={meta} />
      <Stack gap="md">
        {state.regions.length >= 1 ? (
          <form onSubmit={(e) => e.preventDefault()} noValidate={true}>
            <Accordion onValueChange={handleAccordionStateUpdate} value={state.accordionValues}>
              {state.regions.map(({ id: regionId, bandwidth, requests, subtotal, units }) => (
                <AccordionItem key={regionId} value={regionId}>
                  <AccordionTrigger
                    actionSlot={
                      <RemoveButton
                        onClick={() => dispatch({ type: ACTIONS.REMOVE_REGION, region: regionId })}
                        aria-label={meta.microcopy.remove}
                      />
                    }
                  >
                    <MikanSpan text={meta[regionId] || regionId} />
                  </AccordionTrigger>
                  <AccordionContent>
                    <Box pt={"md"} pl={["md", "md", "4xl"]} pr={["md", "md", "xl"]}>
                      <Stack direction="horizontal" gap="xs">
                        <Box flexGrow={1}>
                          <EstimatorGrid>
                            <Input
                              onChange={(e) =>
                                dispatch({
                                  type: ACTIONS.UPDATE_BANDWIDTH,
                                  value: unformat(e.target.value),
                                  region: regionId,
                                  units,
                                })
                              }
                              data-error={bandwidth === 0 ? "true" : "false"}
                              name={`${regionId}-bandwidth`}
                              label={`${meta.microcopy.bandwidth} (${units})`}
                              id={`${regionId}-bandwidth`}
                              value={formatNumber(bandwidth) || ""}
                              error={bandwidth === 0 ? meta.microcopy["form:required-field"] : null}
                            />
                            <Input
                              onChange={(e) =>
                                dispatch({
                                  type: ACTIONS.UPDATE_REQUESTS,
                                  value: unformat(e.target.value),
                                  region: regionId,
                                })
                              }
                              data-error={requests === 0 ? "true" : "false"}
                              name={`${regionId}-requests`}
                              label={meta.microcopy.requests}
                              id={`${regionId}-requests`}
                              value={formatNumber(requests) || ""}
                              error={requests === 0 ? meta.microcopy["form:required-field"] : null}
                            />
                            <CurrencyOutput label={meta.microcopy.cost}>
                              {subtotal < 50 ? formatCurrency(0) : formatCurrency(subtotal)}
                            </CurrencyOutput>
                          </EstimatorGrid>
                        </Box>
                        <Toggletip anchor={viewportIsLarge ? "center" : "right"}>
                          <Stack as="span" gap="xs">
                            <Text variant="displaysm">
                              {meta[regionId] || regionId} {meta.microcopy.toggletipPricing}
                            </Text>
                            <span>
                              <Text variant="sm">{meta.microcopy.toggletipFirst}:</Text>{" "}
                              <Text variant="displaysm">${regionsConfig[regionId].first10TBRate}</Text>
                            </span>
                            <span>
                              <Text variant="sm">{meta.microcopy.toggletipNext}:</Text>{" "}
                              <Text variant="displaysm">${regionsConfig[regionId].next10TBRate}</Text>
                            </span>
                            <span>
                              <Text variant="sm">{meta.microcopy.requests}:</Text>{" "}
                              <Text variant="displaysm">${regionsConfig[regionId].requestsRate}</Text>
                            </span>
                          </Stack>
                        </Toggletip>
                      </Stack>
                    </Box>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </form>
        ) : null}
        <Stack gap="md">
          <div>
            <TotalHeader>
              <MikanSpan text={meta.microcopy.total} />
            </TotalHeader>
            <Box pt={"md"} pl={["md", "md", "4xl"]} pr={["md", "md", "xl"]}>
              <EstimatorGrid>
                <Output label={`${meta.microcopy.bandwidth} (GB)`}>{formatNumber(state.total.bandwidth)}</Output>
                <Output label={meta.microcopy.requests}>{formatNumber(state.total.requests)}</Output>
                <CurrencyOutput label={meta.microcopy.cost}>{formatCurrency(state.total.subtotal)}</CurrencyOutput>
              </EstimatorGrid>
            </Box>
          </div>
          <Stack as="p" direction="horizontal" alignItems="center" justifyContent="flex-end" paddingRight="md">
            <Text as="span">
              <MikanSpan text={meta.microcopy.contact} />
            </Text>
            <Box as="span" flexShrink={0}>
              <Button size="sm" url={withPrefix("/contact-sales")}>
                <MikanSpan text={meta.microcopy.quote} />
              </Button>
            </Box>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  )
}

PricingEstimator.propTypes = {
  meta: {
    north_america: PropTypes.string.required,
    europe: PropTypes.string.required,
    asia: PropTypes.string.required,
    south_america: PropTypes.string.required,
    anz: PropTypes.string.required,
    south_korea: PropTypes.string.required,
    india: PropTypes.string.required,
    africa: PropTypes.string.required,
    microcopy: {
      remove: PropTypes.string.required,
      region: PropTypes.string.required,
      units: PropTypes.string.required,
      add: PropTypes.string.required,
      bandwidth: PropTypes.string.required,
      requests: PropTypes.string.required,
      cost: PropTypes.string.required,
      total: PropTypes.string.required,
      contact: PropTypes.string.required,
      quote: PropTypes.string.required,
      toggletipPricing: PropTypes.string.required,
      toggletipFirst: PropTypes.string.required,
      toggletipNext: PropTypes.string.required,
      toggletipRequests: PropTypes.string.required,
    },
  },
}

function reducer(state, action) {
  const setRegionState = (region, updateValue) => {
    const targetIndex = state.regions.findIndex((r) => r.id === region)

    return [
      ...state.regions.slice(0, targetIndex),
      updateValue(state.regions[targetIndex]),
      ...state.regions.slice(targetIndex + 1),
    ]
  }

  switch (action.type) {
    case ACTIONS.ADD_REGION: {
      const regions = [...state.regions, { ...initialRegionState, id: action.region, units: action.units }]
      return {
        ...state,
        regions,
        accordionValues: [...state.accordionValues, action.region],
      }
    }
    case ACTIONS.REMOVE_REGION: {
      const targetIndex = state.regions.findIndex((r) => r.id === action.region)
      const regions = [...state.regions.slice(0, targetIndex), ...state.regions.slice(targetIndex + 1)]

      const total = calculateTotal(regions)

      return {
        ...state,
        regions,
        total,
      }
    }
    case ACTIONS.ACCORDION_TOGGLE: {
      return {
        ...state,
        accordionValues: action.accordionValues,
      }
    }
    case ACTIONS.UPDATE_BANDWIDTH: {
      const regions = setRegionState(action.region, (prevRegionState) => ({
        ...prevRegionState,
        bandwidth: action.value,
        subtotal: calculateSubtotal(
          action.units === UNITS.TB ? action.value * 1000 : action.value,
          prevRegionState.requests,
          regionsConfig[action.region]
        ),
      }))

      const total = calculateTotal(regions)

      return {
        ...state,
        regions,
        total,
      }
    }
    case ACTIONS.UPDATE_REQUESTS: {
      const regions = setRegionState(action.region, (prevRegionState) => ({
        ...prevRegionState,
        requests: action.value,
        subtotal: calculateSubtotal(
          prevRegionState.units === UNITS.TB ? prevRegionState.bandwidth * 1000 : prevRegionState.bandwidth,
          action.value,
          regionsConfig[action.region]
        ),
      }))

      const total = calculateTotal(regions)

      return {
        ...state,
        regions,
        total,
      }
    }
    default:
      return state
  }
}

export function calculateSubtotal(bandwidth, requests, { first10TBRate, next10TBRate, requestsRate } = RATES[1]) {
  if (bandwidth === 0 || requests === 0) {
    return 0
  }

  let bandwidthTotal = 0

  if (bandwidth < 10000) {
    bandwidthTotal += bandwidth * first10TBRate
  } else {
    const overage = bandwidth - 10000
    bandwidthTotal += 10000 * first10TBRate + overage * next10TBRate
  }
  const requestsTotal = parseFloat(((requests / 10000) * requestsRate).toFixed(2))
  const total = bandwidthTotal + requestsTotal

  return Math.max(total, 0)
}

export function calculateTotal(regions) {
  if (regions.length < 1) return initialRegionState

  const total = regions.reduce((totalState, region) => {
    const inboundBandwidth = region.units === UNITS.TB ? region.bandwidth * 1000 : region.bandwidth
    return {
      ...totalState,
      bandwidth: totalState.bandwidth + inboundBandwidth,
      requests: totalState.requests + region.requests,
      subtotal: totalState.subtotal + region.subtotal,
    }
  }, initialRegionState)

  return {
    ...total,
    subtotal: Math.max(total.subtotal, 50),
  }
}

function formatNumber(n) {
  if (n !== 0 && !n) return null

  return new Intl.NumberFormat("en-US").format(n)
}

export function formatCurrency(n) {
  if (n !== 0 && !n) return null

  const displayDecimal = n < 1000

  const opts = displayDecimal ? CURRENCY_FORMAT : {}

  const removeCurrencySymbol = ({ type }) => type !== "currency"

  return new Intl.NumberFormat("en-US", opts)
    .formatToParts(displayDecimal ? n : Math.floor(n))
    .filter(removeCurrencySymbol)
    .map(({ value }) => value)
    .join("")
}

function unformat(str) {
  if (!str) return 0
  return parseInt(str.replaceAll(",", ""), 10) || 0
}

export default PricingEstimator
